import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getEmployeeDetails, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaArrowLeft, FaEdit, FaTrash, FaUser, FaCalendarAlt, FaEnvelope, FaPhone, FaBuilding, FaBriefcase, FaClock, FaImage, FaIdCard } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// employee details component 
const EmployeeDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { employeeDetails, isLoading, error, success } = useSelector((state) => state.auth);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';
  const locale = 'en-US';
  const lastErrorRef = useRef({ message: null, time: 0 });

  useEffect(() => {
    if (id) {
      dispatch(getEmployeeDetails(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
      toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  const handleEdit = () => {
    navigate(`/edit-employee/${id}`);
  };

  const handleDelete = () => {
    navigate(`/employees`);
  };

  const handleBack = () => {
    navigate('/employees');
  };

  if (isLoading) {
    return (
      <div style={{ 
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: dir,
        textAlign
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('employees.common.loading')}</span>
        </div>
      </div>
    );
  }

  if (!employeeDetails) {
    return (
      <div style={{ 
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: dir,
        textAlign
      }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#ffc107', marginBottom: '20px' }}></i>
          <h5 style={{ color: 'white', marginBottom: '10px' }}>{t('employees.details.notFoundTitle')}</h5>
          <p style={{ color: '#6c757d', marginBottom: '20px' }}>{t('employees.details.notFoundDescription')}</p>
          <button
            onClick={handleBack}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 25px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
          >
            <FaArrowLeft />
            {t('employees.details.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      textAlign
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #333'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1f2e'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 style={{ 
                margin: 0, 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: 'white',
                textAlign
              }}>
                {t('employees.details.title')}
              </h1>
              <button
                onClick={handleBack}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FaArrowLeft />
                {t('employees.details.back')}
              </button>
            </div>
          </div>

          <div style={{ padding: '30px' }}>
            {/* صورة الموظف والمعلومات الأساسية */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{
                backgroundColor: '#1a1f2e',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #333'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <FaUser style={{ fontSize: '1.2rem', color: '#AC2000', marginInlineStart: isRTL ? '10px' : '0', marginInlineEnd: isRTL ? '0' : '10px' }} />
                  <h3 style={{ margin: 0, color: 'white', fontSize: '18px' }}>{t('employees.details.sections.personal')}</h3>
                </div>
                
                <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                  {/* صورة الموظف */}
                  <div style={{ textAlign: 'center' }}>
                    {employeeDetails.images && employeeDetails.images.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', maxWidth: '260px' }}>
                        {employeeDetails.images.map((img, i) => (
                          <a key={img.id || i} href={img.url} target="_blank" rel="noopener noreferrer" title={t('employees.details.imageAlt')}>
                            <img
                              src={img.url}
                              alt={t('employees.details.imageAlt') + ' ' + (i + 1)}
                              style={{
                                width: '120px',
                                height: '120px',
                                borderRadius: '12px',
                                objectFit: 'cover',
                                border: '3px solid #AC2000',
                                cursor: 'pointer',
                                display: 'block'
                              }}
                            />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        backgroundColor: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '3px solid #AC2000'
                      }}>
                        <FaImage style={{ fontSize: '2rem', color: '#666' }} />
                      </div>
                    )}
                  </div>

                  {/* المعلومات الأساسية */}
                  <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '5px',
                        color: '#6c757d',
                        fontSize: '14px'
                      }}>
                        {t('employees.details.fields.name')}
                      </label>
                      <p style={{ 
                        margin: 0, 
                        color: 'white', 
                        fontSize: '18px', 
                        fontWeight: 'bold' 
                      }}>
                        {employeeDetails.name}
                      </p>
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '5px',
                        color: '#6c757d',
                        fontSize: '14px'
                      }}>
                        {t('employees.details.fields.code')}
                      </label>
                      <p style={{ 
                        margin: 0, 
                        color: 'white', 
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaIdCard />
                        {employeeDetails.employee_code}
                      </p>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '5px',
                        color: '#6c757d',
                        fontSize: '14px'
                      }}>
                        {t('employees.details.fields.username')}
                      </label>
                      <p style={{ 
                        margin: 0, 
                        color: 'white', 
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaUser />
                        {employeeDetails.username || t('employees.common.notFound', { defaultValue: 'Not found' })}
                      </p>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '5px',
                        color: '#6c757d',
                        fontSize: '14px'
                      }}>
                        {t('employees.details.fields.nationalId')}
                      </label>
                      <p style={{ 
                        margin: 0, 
                        color: 'white', 
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaIdCard />
                        {employeeDetails.national_id || '—'}
                      </p>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '5px',
                        color: '#6c757d',
                        fontSize: '14px'
                      }}>
                        {t('employees.details.fields.email')}
                      </label>
                      <p style={{ 
                        margin: 0, 
                        color: 'white', 
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaEnvelope />
                        {employeeDetails.email}
                      </p>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '5px',
                        color: '#6c757d',
                        fontSize: '14px'
                      }}>
                        {t('employees.details.fields.phone')}
                      </label>
                      <p style={{ 
                        margin: 0, 
                        color: 'white', 
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaPhone />
                        {employeeDetails.phone}
                      </p>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '5px',
                        color: '#6c757d',
                        fontSize: '14px'
                      }}>
                        {t('employees.details.fields.birthDate')}
                      </label>
                      <p style={{ 
                        margin: 0, 
                        color: 'white', 
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaCalendarAlt />
                        {new Date(employeeDetails.birth_date).toLocaleDateString(locale, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block',
                        marginBottom: '5px',
                        color: '#6c757d',
                        fontSize: '14px'
                      }}>
                        {t('employees.details.fields.gender')}
                      </label>
                      <p style={{ 
                        margin: 0, 
                        color: 'white', 
                        fontSize: '14px'
                      }}>
                        {employeeDetails.gender === 'female'
                          ? t('employees.common.gender.female')
                          : t('employees.common.gender.male')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* المعلومات الوظيفية */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{
                backgroundColor: '#1a1f2e',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #333'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <FaBriefcase style={{ fontSize: '1.2rem', color: '#17a2b8', marginInlineStart: isRTL ? '10px' : '0', marginInlineEnd: isRTL ? '0' : '10px' }} />
                  <h3 style={{ margin: 0, color: 'white', fontSize: '18px' }}>{t('employees.details.sections.job')}</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '5px',
                      color: '#6c757d',
                      fontSize: '14px'
                    }}>
                      {t('employees.details.fields.department')}
                    </label>
                    <p style={{ 
                      margin: 0, 
                      color: 'white', 
                      fontSize: '16px', 
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaBuilding />
                      {employeeDetails.department}
                    </p>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '5px',
                      color: '#6c757d',
                      fontSize: '14px'
                    }}>
                      {t('employees.details.fields.position')}
                    </label>
                    <p style={{ 
                      margin: 0, 
                      color: 'white', 
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaBriefcase />
                      {employeeDetails.position}
                    </p>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '5px',
                      color: '#6c757d',
                      fontSize: '14px'
                    }}>
                      {t('employees.details.fields.shift')}
                    </label>
                    <p style={{ 
                      margin: 0, 
                      color: 'white', 
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaClock />
                      {employeeDetails.work_shift ? employeeDetails.work_shift.name : t('employees.details.noShift')}
                    </p>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '5px',
                      color: '#6c757d',
                      fontSize: '14px'
                    }}>
                      {t('employees.details.fields.status')}
                    </label>
                    <p style={{ 
                      margin: 0, 
                      color: 'white', 
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      {employeeDetails.is_active ? t('employees.common.status.active') : t('employees.common.status.inactive')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* تاريخ الإنشاء */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{
                backgroundColor: '#1a1f2e',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #333'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <FaCalendarAlt style={{ fontSize: '1.2rem', color: '#ffc107', marginInlineStart: isRTL ? '10px' : '0', marginInlineEnd: isRTL ? '0' : '10px' }} />
                  <h3 style={{ margin: 0, color: 'white', fontSize: '18px' }}>{t('employees.details.sections.additional')}</h3>
                </div>
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '5px',
                    color: '#6c757d',
                    fontSize: '14px'
                  }}>
                    {t('employees.details.fields.createdAt')}
                  </label>
                  <p style={{ 
                    margin: 0, 
                    color: 'white', 
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FaCalendarAlt />
                    {new Date(employeeDetails.created_at).toLocaleDateString(locale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* الإجراءات */}
            <div>
              <div style={{
                backgroundColor: '#1a1f2e',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #333'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '18px' }}>{t('employees.details.sections.actions')}</h3>
                </div>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <button
                    onClick={handleEdit}
                    style={{
                      backgroundColor: '#B3B3B3',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FaEdit />
                    {t('employees.details.buttons.edit')}
                  </button>
                  <button
                    onClick={handleDelete}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FaTrash />
                    {t('employees.details.buttons.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetails;
